<template>
  <div
    class="tag-search-append"
    @click.stop="handleClick"
    @pointerdown.stop="handleClick"
    @mousedown.stop="handleClick"
    @mouseup.stop="handleClick"
  >
    <div
      v-if="hasClear"
      class="tag-search-append__icon-clear-wrapper"
      @click.stop="onClear"
    >
      <ui-icon size="12" :color="$colors.red" class="tag-search-append__icon-clear mr-4" icon="fas fa-times"></ui-icon>
    </div>

    <template v-if="!hideTags">
      <ui-button
        style="min-width: 45px"
        :icon="selectedItem.iconAppend"
        size="mini"
        :has-menu="true"
        :force-chevron="true"
        :menu-props="{
          menuWidth: 0,
          selected: selected,
        }"
        :menu-list="pItems"
        menu-content-class="tag-search-append__menu"
        @click="handleClick"
      ></ui-button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useColors, useI18n } from '@/composables';

export interface TagSearchAppendItem {
  name: string;
  icon: string;
  showCondition: boolean;
}

const props = withDefaults(
  defineProps<{
    hasClear?: boolean;
    hasMarks?: boolean;
    hideTags?: boolean;
    selected?: string;
  }>(),
  {
    hasClear: false,
    hasMarks: false,
    hideTags: false,
    selected: '',
  },
);

const emit = defineEmits<{
  'update:selected': [value: string];
  clear: [];
  click: [];
}>();

const route = useRoute();
const { t } = useI18n();
const $colors = useColors();

const tagsPlanLimit = computed((): keyof BillingLimits | undefined => {
  switch (route.params.type) {
    case 'campaigns':
      return 'tagsCampaigns';
    case 'offers':
      return 'tagsOffers';
    case 'landings':
      return 'tagsLandings';
    case 'flows':
      return 'tagsFlows';
    case 'affiliate-networks':
      return 'tagsAffiliateNetworks';
    case 'traffic-sources':
      return 'tagsTrafficSources';
  }
});

const marksPlanLimit = computed((): keyof BillingLimits | null => {
  if (String(route.params?.type).includes('custom')) return 'markersCustomParams';
  switch (route.params?.type) {
    case 'campaigns':
      return 'markersCampaigns';
    case 'offers':
      return 'markersOffers';
    case 'landings':
      return 'markersLandings';
    case 'flows':
      return 'markersFlows';
    case 'affiliate-networks':
      return 'markersAffiliateNetworks';
    case 'traffic-sources':
      return 'markersTrafficSources';
    default:
      return null;
  }
});

const items = computed((): Bemob.ListItem[] => [
  {
    key: 'text-and',
    title: t('Text (AND)'),
    iconAppend: 'fas fa-text',
    showCondition: true,
  },
  {
    key: 'text-or',
    title: t('Text (OR)'),
    iconAppend: 'fas fa-text',
    showCondition: true,
  },
  {
    key: 'tags-and',
    title: t('Tags (AND)'),
    iconAppend: 'fas fa-tags',
    showCondition: true,
    planLimits: tagsPlanLimit.value,
  },
  {
    key: 'tags-or',
    title: t('Tags (OR)'),
    iconAppend: 'fas fa-tags',
    showCondition: true,
    planLimits: tagsPlanLimit.value,
  },
  {
    key: 'marks-include',
    title: t('Marks (Inc)'),
    iconAppend: 'fas fa-heart',
    showCondition: props.hasMarks,
    planLimits: marksPlanLimit.value,
  },
  {
    key: 'marks-exclude',
    title: t('Marks (Exc)'),
    iconAppend: 'fas fa-heart',
    showCondition: props.hasMarks,
    planLimits: marksPlanLimit.value,
  },
]);

const selectedItem = computed(
  () => items.value.find((item) => item.key === props.selected) || {},
);

const pItems = computed((): Bemob.ListItem[] =>
  items.value
    .map((item) => ({
      ...item,
      click: () => handleItemClick(item),
    }))
    .filter((item) => item.showCondition),
);

const onUpdateSelected = (value: string) => {
  emit('update:selected', value);
};

const onClear = () => {
  emit('clear');
};

const handleItemClick = (item: Bemob.ListItem) => {
  onUpdateSelected(item.key);
};

const handleClick = () => {
  emit('click');
};
</script>

<style lang="scss">
  .tag-search-append {
    height: 22px;
    display: flex;

    &__menu {
      .ui-menu-content-item__text {
        text-transform: none;
      }
    }

    &__item {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 24px;
      border: solid 1px var(--color-gray20);

      cursor: pointer;

      &--selected {
        border-color: var(--color-green);
        background-color: var(--color-green);

        &:hover {
          position: relative;
          &:before {
            content: '';
            position: absolute;
            height: 100%;
            width: 100%;
            background-color: var(--color-gray5);
            opacity: 1;
          }
        }
      }

      &--avaliable {
        &:hover {
          background-color: var(--color-green10);
        }
      }

      &--first {
        border-radius: 4px 0 0 4px;
      }

      &--last {
        border-radius: 0 4px 4px 0;
      }
    }

    &__icon-clear-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      cursor:pointer;
  }

    & &__icon-clear.v-icon {
      cursor: pointer;
      line-height: 1.09;
    }

    & &__item-icon.v-icon {
      font-size: 11px;
      line-height: 1.09;
      color: var(--color-graphite) !important;
    }

    & &__item--selected &__item-icon {
      color: var(--color-white) !important;
    }
  }
</style>
